// frontend/src/pages/portfolio/sections/PricingSection.tsx
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, CheckCircle2, Sparkles, Tag } from "lucide-react";
import SectionEyebrow from "../components/SectionEyebrow";
import {
  cardHover,
  container,
  sectionHeading,
  sectionLede,
  sectionReveal,
} from "../components/styles";

const freeFeatures = [
  "Unlimited clock-in / clock-out & session history",
  "Role-based admin & employee dashboards",
  "Calendar & week overview",
  "Activity tracking (desktop)",
  "Screenshots",
  "Billing hours entry",
];

const aiFeatures = [
  "AI daily-summary validation",
  "AI session analysis & employee insights",
];

const PricingSection: React.FC = () => {
  return (
    <section
      id="pricing"
      className="relative border-t border-primary-200/30 bg-white py-16 sm:py-20"
      aria-labelledby="pricing-heading"
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 mx-auto h-64 max-w-5xl bg-gradient-to-b from-primary-100/40 via-transparent to-transparent blur-2xl motion-reduce:hidden"
        aria-hidden
      />
      <div className={container}>
        <div className={`max-w-2xl ${sectionReveal}`}>
          <SectionEyebrow icon={Tag}>Pricing</SectionEyebrow>
          <h2 id="pricing-heading" className={`mt-4 ${sectionHeading}`}>
            Free to use.{" "}
            <span className="relative whitespace-nowrap">
              AI
              <span
                className="absolute -bottom-1 left-0 right-0 h-2.5 rounded-sm bg-gradient-to-r from-primary-300/70 to-primary-100/0 -z-10"
                aria-hidden
              />
            </span>{" "}
            is the only paid part.
          </h2>
          <p className={sectionLede}>
            The full time-tracking platform is free. Pay only if you want
            AI-powered summaries and analysis.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {/* Free */}
          <article className={`relative flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-card ring-1 ring-primary-900/[0.04] sm:p-8 ${cardHover} ${sectionReveal} motion-safe:delay-75`}>
            <div className="flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-2 rounded-full bg-success-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-success-700 ring-1 ring-success-200/80">
                <Check className="h-3.5 w-3.5" aria-hidden />
                Free forever
              </span>
              <span className="text-xs font-medium text-gray-500">No card required</span>
            </div>
            <h3 className="mt-6 font-display text-2xl font-semibold text-primary-950">
              Core platform
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              Everything teams need to track time and stay aligned — at no cost.
            </p>
            <ul className="mt-6 space-y-3">
              {freeFeatures.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success-600" aria-hidden />
                  <span className="text-sm leading-relaxed text-gray-700">{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 border-t border-gray-100 pt-6">
              <Link
                to="/register-org"
                className="btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                Get started free
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </article>

          {/* AI */}
          <article className={`relative flex flex-col overflow-hidden rounded-2xl border border-primary-900/10 bg-primary-900 p-6 text-white shadow-elevated sm:p-8 ${cardHover} ${sectionReveal} motion-safe:delay-150`}>
            <div
              className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary-400/15 blur-3xl motion-reduce:hidden"
              aria-hidden
            />
            <div className="relative flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-100 ring-1 ring-white/15 backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                Optional · Paid
              </span>
              <span className="text-xs font-medium text-primary-200/90">Pay as you go</span>
            </div>
            <h3 className="relative mt-6 font-display text-2xl font-semibold text-white">
              AI add-on
            </h3>
            <p className="relative mt-2 text-sm leading-relaxed text-primary-100/90">
              Layer intelligent review and analysis on top of your team&apos;s
              time data — only when you need it.
            </p>
            <ul className="relative mt-6 space-y-3">
              {aiFeatures.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary-200" aria-hidden />
                  <span className="text-sm leading-relaxed text-primary-50">{f}</span>
                </li>
              ))}
            </ul>
            <div className="relative mt-8 border-t border-white/10 pt-6">
              <p className="text-xs font-medium text-primary-200/90">
                Pay only for what you use.
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
